import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AccountsReceivableItem,
  AccountsReceivableSliceState,
  AccReceivableBodyType,
  AccReceivableResponse,
  ARFilterItemType,
  ARFilterType,
} from "./types";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { PageSizeType } from "@/app/model/types";

const initialState: AccountsReceivableSliceState = {
  items: [],
  groupedItemsByDealer: null,
  actualDate: null,
  error: null,
  isLoading: false,
  isFilterLoading: false,
  totalCount: 0,
  pageSize: (+localStorage.getItem("psizeAR") as PageSizeType) || 20,
  searchCode: null,
  selectedDates: undefined,
  selectedARFilters: {},
  onlyMyDealers: false,
  filters: [],
};

// Запросы
export const fetchAccountsReceivable = createAsyncThunk<
  AccReceivableResponse,
  AccReceivableBodyType,
  { rejectValue: string }
>(
  "accountsreceivable/fetchAccountsReceivable",
  async (body, { rejectWithValue }) => {
    try {
      const response = await $api.post<AccReceivableResponse>(
        "/accountsreceivable",
        body,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchActualDate = createAsyncThunk<
  { value: string },
  void,
  { rejectValue: string }
>("accountsreceivable/fetchActualDate", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<{ value: string }>(
      "/accountsreceivable/actual_date",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchFilters = createAsyncThunk(
  "accountsreceivable/fetchFilters",

  async (fields: ARFilterItemType | [] = [], { rejectWithValue }) => {
    const body = {
      filters: Object.keys(fields).length ? [{ type: "eq", fields }] : [],
    };

    try {
      const response = await $api.post("/filters/accountsreceivable", body);
      return response.data?.filters;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const receivableSlice = createSlice({
  name: "accountsReceivable",
  initialState,
  reducers: {
    setSearchCode(state, action: PayloadAction<string | null>) {
      state.selectedDates = [];
      state.selectedARFilters = {};
      state.searchCode = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    setOnlyMyDealers(state, action) {
      state.onlyMyDealers = action.payload;
    },
    setSelectedDates(state, action) {
      state.searchCode = null;
      state.selectedDates = action.payload;
    },
    setSelectedARFilters(state, action) {
      state.searchCode = null;
      state.selectedARFilters = action.payload;
    },
    resetFilters(state) {
      state.searchCode = null;
      state.selectedDates = undefined;
      state.selectedARFilters = {};
      state.onlyMyDealers = false;
    },
  },

  extraReducers(builder) {
    // Загрузка текущих данных
    builder.addCase(fetchAccountsReceivable.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAccountsReceivable.fulfilled, (state, action) => {
      state.isLoading = false;
      const items = action.payload.items;

      if (Array.isArray(items)) {
        state.groupedItemsByDealer = items?.reduce(
          (
            acc: {
              [dealerId: string]: {
                dealerShortName: string;
                items: AccountsReceivableItem[];
              };
            },
            item: AccountsReceivableItem,
          ) => {
            if (acc[item.mainDealerId]) {
              const data = acc[item.mainDealerId];
              acc[item.mainDealerId] = {
                dealerShortName: item.mainDealerShortName,
                items: [...data.items, item],
              };
            } else {
              acc[item.mainDealerId] = {
                dealerShortName: item.mainDealerShortName,
                items: [item],
              };
            }
            return acc;
          },
          {},
        );
      }

      state.totalCount = action.payload.totalCount;
    });
    builder.addCase(fetchAccountsReceivable.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    // Загрузка актуальной даты
    builder.addCase(fetchActualDate.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchActualDate.fulfilled, (state, action) => {
      state.isLoading = false;
      state.actualDate = action.payload.value;
    });
    builder.addCase(fetchActualDate.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(fetchFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(
      fetchFilters.fulfilled,
      (state, action: PayloadAction<ARFilterType>) => {
        state.filters = action.payload;
        state.isFilterLoading = false;
      },
    );

    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload as string;
    });
  },
});
export const {
  setSearchCode,
  setPageSize,
  setSelectedDates,
  setSelectedARFilters,
  setOnlyMyDealers,
  resetFilters,
} = receivableSlice.actions;
export default receivableSlice.reducer;
